'use client'

import React, { useState } from 'react';
import './match-card.css';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faClock, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '@/components/utils/analytics';
import { stageMeta } from './stage';

const isToday = (date) => {
  const ist = { timeZone: 'Asia/Kolkata' };
  const matchStr = new Date(date).toLocaleDateString('en-CA', ist);
  const todayStr = new Date().toLocaleDateString('en-CA', ist);
  return matchStr === todayStr;
};

const MatchCard = ({
  index = 0, city, date, matchNumber, time, venue,
  team1, team2, won, status, stage, logoClass, winnerPoints, matchBonusPoints, matchRevotePoints,
  bonusData,
}) => {
  const [expanded, setExpanded] = useState(false);

  const isAbandoned = status === 'abandoned';
  const isCompleted = !!won;
  const todayMatch = !isAbandoned && isToday(date);
  const hasBonusData = bonusData && bonusData.question;
  const winnerTeam = won || null;
  const animationDelay = `${(0.1 + index * 0.07).toFixed(2)}s`;
  const meta = stageMeta(stage);
  const stageClass = meta ? `sched-row--playoff sched-row--${meta.key}` : '';

  return (
    <div
      className={`sched-row ${isAbandoned ? 'sched-row--abandoned' : ''} ${todayMatch ? 'sched-row--today' : ''} ${hasBonusData ? 'sched-row--clickable' : ''} ${stageClass} animate__animated animate__fadeIn`}
      style={{ animationDelay }}
      onClick={() => { if (hasBonusData) { setExpanded(e => { const next = !e; trackEvent('expand_match_card', { match_number: matchNumber, expanded: next }); return next; }); } }}
    >
      {/* Match number watermark */}
      <span className="sched-row__num">{matchNumber}</span>

      {meta && (
        <span className="sched-row__stage-emboss" aria-label={meta.label}>
          {meta.label}
        </span>
      )}

      {/* Main content row */}
      <div className="sched-row__main">
        {/* Left: teams + meta */}
        <div className="sched-row__left">
          <div className="sched-row__teams">
            <div className={`sched-row__team ${!isAbandoned && won && team1 === won ? 'sched-row__team--won' : ''} ${!isAbandoned && won && team1 !== won ? 'sched-row__team--lost' : ''}`}>
              <span className={`${logoClass} ${team1} sched-row__flag`} />
              <span className="sched-row__code">{team1.toUpperCase()}</span>
            </div>
            <span className="sched-row__vs">vs</span>
            <div className={`sched-row__team ${!isAbandoned && won && team2 === won ? 'sched-row__team--won' : ''} ${!isAbandoned && won && team2 !== won ? 'sched-row__team--lost' : ''}`}>
              <span className={`${logoClass} ${team2} sched-row__flag`} />
              <span className="sched-row__code">{team2.toUpperCase()}</span>
            </div>
          </div>

          <div className="sched-row__meta">
            <span className="sched-row__time">
              <FontAwesomeIcon icon={faClock} />
              {time}, {moment(date).format('ddd, DD MMM')}
            </span>
            <span className="sched-row__venue-text">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {venue}{city ? `, ${city}` : ''}
            </span>
          </div>
        </div>

        {/* Right: points */}
        <div className="sched-row__right">
          {isAbandoned ? (
            <span className="sched-row__no-result">No Result</span>
          ) : isCompleted ? (
            <>
              {/* Points */}
              <div className="sched-row__pts">
                {((winnerPoints || 0) + (matchRevotePoints || 0)) > 0 && (
                  <div className="sched-row__pts-row">
                    <span className="sched-row__pts-label">Match</span>
                    <span className="sched-row__match-pts">+{((winnerPoints || 0) + (matchRevotePoints || 0)).toFixed(2).replace(/\.?0+$/, '')}</span>
                  </div>
                )}
                {matchBonusPoints != null && matchBonusPoints > 0 && (
                  <div className="sched-row__pts-row">
                    <span className="sched-row__pts-label">Bonus</span>
                    <span className="sched-row__bonus-val">+{matchBonusPoints}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <span className="sched-row__upcoming">Upcoming</span>
          )}
          {hasBonusData && (
            <div className={`sched-row__expand ${expanded ? 'sched-row__expand--open' : ''}`}>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          )}
        </div>
      </div>

      {/* Expanded: bonus question (with animation) */}
      {hasBonusData && (
        <div className={`sched-row__bonus-wrap ${expanded ? 'sched-row__bonus-wrap--open' : ''}`}>
          <div className="sched-row__bonus-inner">
            <div className="sched-row__bonus" onClick={(e) => e.stopPropagation()}>
              <p className="sched-row__bonus-q">{bonusData.question}</p>
              <div className="sched-row__bonus-opts">
                {bonusData.options.map((opt, i) => {
                  const isCorrectOpt = bonusData.correctAnswer && opt === bonusData.correctAnswer;
                  return (
                    <div key={i} className={`sched-row__opt ${isCorrectOpt ? 'sched-row__opt--correct' : ''}`}>
                      <span className="sched-row__opt-text">{opt}</span>
                      {isCorrectOpt && (
                        <span className="sched-row__opt-badge">
                          <FontAwesomeIcon icon={faCheck} /> Answer
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {!bonusData.correctAnswer && (
                <p className="sched-row__bonus-pending">Result pending</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchCard;
